import { generateProjectId } from '../api/design/projectSupport';

/**
 * Migrate old flat design structure to new project-based structure
 * @param {Object} oldData - Old localStorage data with flat design structure
 * @returns {Object} New project-based structure
 */
export function migrateToProjectStructure(oldData) {
  // Support Zustand persist format and legacy
  const data = oldData?.state ? oldData.state : oldData;

  if (!data || !data.designs) {
    return {
      projects: {},
      designs: {},
      designIds: [],
      currentProjectId: null,
      selectedDesignId: null
    };
  }

  const { designs, designIds, selectedDesignId } = data;
  const projectsMap = new Map();
  const migratedDesigns = {};
  const migratedDesignIds = [];

  // Group designs by DesignStyleChoiceid
  Object.keys(designs).forEach((designId) => {
    const design = designs[designId];
    const styleChoiceId = design.DesignStyleChoiceid;
    if (!styleChoiceId) {
      // Skip designs without style choice ID
      return;
    }
    const projectId = generateProjectId(styleChoiceId);
    // Create or update project
    if (!projectsMap.has(projectId)) {
      projectsMap.set(projectId, {
        id: projectId,
        styleChoiceId,
        name: `Style Choice ${styleChoiceId} Project`,
        created: design.Created || new Date().toISOString(),
        lastModified: design.LastModified || new Date().toISOString(),
        designIds: []
      });
    }
    // Add design to project
    const project = projectsMap.get(projectId);
    project.designIds.push(designId);
    // Update project timestamps
    if (design.Created && design.Created < project.created) {
      project.created = design.Created;
    }
    if (design.LastModified && design.LastModified > project.lastModified) {
      project.lastModified = design.LastModified;
    }
    // Migrate design with project reference
    migratedDesigns[designId] = {
      ...design,
      projectId
    };
    migratedDesignIds.push(designId);
  });
  // Convert projects map to object
  const projects = {};
  projectsMap.forEach((project, projectId) => {
    projects[projectId] = project;
  });
  // Determine current project and selected design
  const selectedDesignProjectId = selectedDesignId && migratedDesigns[selectedDesignId]
    ? migratedDesigns[selectedDesignId].projectId
    : null;
  const currentProjectId = selectedDesignProjectId
    || (Object.keys(projects).length > 0 ? Object.keys(projects)[0] : null);
  return {
    projects,
    designs: migratedDesigns,
    designIds: migratedDesignIds,
    currentProjectId,
    selectedDesignId: selectedDesignId || null,
    // Keep migration metadata
    _migrationInfo: {
      migratedAt: new Date().toISOString(),
      originalStructure: 'flat',
      migratedStructure: 'project-based',
      totalDesigns: Object.keys(migratedDesigns).length,
      totalProjects: Object.keys(projects).length
    }
  };
}

/**
 * Group existing designs by DesignStyleChoiceid
 * @param {Object} designs - Designs object
 * @returns {Object} Grouped designs by style choice ID
 */
export const groupDesignsByStyleChoice = (designs) => {
  const grouped = {};

  Object.keys(designs).forEach((designId) => {
    const design = designs[designId];
    const styleChoiceId = design.DesignStyleChoiceid;

    if (styleChoiceId) {
      if (!grouped[styleChoiceId]) {
        grouped[styleChoiceId] = [];
      }
      grouped[styleChoiceId].push({ ...design, id: designId });
    }
  });

  return grouped;
};

/**
 * Create project metadata from a group of designs
 * @param {number} styleChoiceId - Style choice ID
 * @param {Array} designs - Array of designs
 * @returns {Object} Project metadata
 */
export const createProjectFromDesigns = (styleChoiceId, designs) => {
  if (!designs || designs.length === 0) {
    return null;
  }

  const projectId = generateProjectId(styleChoiceId);
  const designIds = designs.map((design) => design.id || design.Id);

  // Find earliest creation date and latest modification date
  const createdDates = designs.map((design) => design.Created).filter(Boolean);
  const modifiedDates = designs.map((design) => design.LastModified).filter(Boolean);

  const created = createdDates.length > 0
    ? createdDates.sort()[0]
    : new Date().toISOString();

  const lastModified = modifiedDates.length > 0
    ? modifiedDates.sort().reverse()[0]
    : new Date().toISOString();

  // Generate project name based on design names
  const designNames = designs.map((design) => design.DesignName).filter(Boolean);
  const uniqueNames = [...new Set(designNames)];
  const projectName = uniqueNames.length > 0
    ? `${uniqueNames.join(', ')} Project`
    : `Style Choice ${styleChoiceId} Project`;

  return {
    id: projectId,
    styleChoiceId,
    name: projectName,
    created,
    lastModified,
    designIds,
    designCount: designs.length
  };
};

/**
 * Validate migration results
 * @param {Object} oldData - Original data
 * @param {Object} newData - Migrated data
 * @returns {Object} Validation results
 */
export const validateMigration = (oldData, newData) => {
  // Support Zustand persist format and legacy for both old and new data
  const data = oldData?.state ? oldData.state : oldData;
  const migrated = newData?.state ? newData.state : newData;
  const validation = {
    isValid: true,
    errors: [],
    warnings: [],
    summary: {}
  };

  // Check design count
  const oldDesignCount = data.designs ? Object.keys(data.designs).length : 0;
  const newDesignCount = migrated.designs ? Object.keys(migrated.designs).length : 0;

  validation.summary.originalDesigns = oldDesignCount;
  validation.summary.migratedDesigns = newDesignCount;
  validation.summary.totalProjects = migrated.projects ? Object.keys(migrated.projects).length : 0;

  if (oldDesignCount !== newDesignCount) {
    validation.errors.push(
      `Design count mismatch: ${oldDesignCount} original vs ${newDesignCount} migrated`
    );
    validation.isValid = false;
  }

  // Check that all designs have projectId
  if (migrated.designs) {
    Object.keys(migrated.designs).forEach((designId) => {
      const design = migrated.designs[designId];
      if (!design.projectId) {
        validation.errors.push(`Design ${designId} missing projectId`);
        validation.isValid = false;
      }
    });
  }

  // Check that all projects have valid designIds
  if (migrated.projects) {
    Object.keys(migrated.projects).forEach((projectId) => {
      const project = migrated.projects[projectId];
      if (!project.designIds || project.designIds.length === 0) {
        validation.warnings.push(`Project ${projectId} has no designs`);
      }

      project.designIds.forEach((designId) => {
        if (!migrated.designs[designId]) {
          validation.errors.push(`Project ${projectId} references non-existent design ${designId}`);
          validation.isValid = false;
        }
      });
    });
  }

  // Check selectedDesignId is valid
  if (migrated.selectedDesignId && !migrated.designs[migrated.selectedDesignId]) {
    validation.errors.push(`Selected design ${migrated.selectedDesignId} does not exist`);
    validation.isValid = false;
  }

  // Check currentProjectId is valid
  if (migrated.currentProjectId && !migrated.projects[migrated.currentProjectId]) {
    validation.errors.push(`Current project ${migrated.currentProjectId} does not exist`);
    validation.isValid = false;
  }

  return validation;
};

/**
 * Check if localStorage needs migration
 * @param {Object} data - localStorage data
 * @returns {boolean} True if migration is needed
 */
export const needsMigration = (data) => {
  if (!data) return false;

  // If it already has projects structure, no migration needed
  if (data.projects) return false;

  // If it has designs but no projects, migration is needed
  if (data.designs && Object.keys(data.designs).length > 0) return true;

  return false;
};

/**
 * Add a design to a project (for new design creation)
 * @param {Object} state - Current state
 * @param {string} designId - Design ID to add
 * @param {string} projectId - Project ID to add to
 * @returns {Object} Updated state
 */
export const addDesignToProject = (state, designId, projectId) => {
  const { projects = {}, designs = {} } = state;

  // Update project
  const updatedProjects = { ...projects };
  if (updatedProjects[projectId]) {
    updatedProjects[projectId] = {
      ...updatedProjects[projectId],
      designIds: [...new Set([...updatedProjects[projectId].designIds, designId])],
      lastModified: new Date().toISOString()
    };
  }

  // Update design
  const updatedDesigns = { ...designs };
  if (updatedDesigns[designId]) {
    updatedDesigns[designId] = {
      ...updatedDesigns[designId],
      projectId
    };
  }

  return {
    ...state,
    projects: updatedProjects,
    designs: updatedDesigns
  };
};

/**
 * Remove a design from a project
 * @param {Object} state - Current state
 * @param {string} designId - Design ID to remove
 * @returns {Object} Updated state
 */
export const removeDesignFromProject = (state, designId) => {
  const { projects = {}, designs = {} } = state;
  const design = designs[designId];

  if (!design || !design.projectId) {
    return state;
  }

  const projectId = design.projectId;
  const project = projects[projectId];

  if (!project) {
    return state;
  }

  // Remove design from project
  const updatedDesignIds = project.designIds.filter((id) => id !== designId);
  const updatedProjects = { ...projects };

  if (updatedDesignIds.length === 0) {
    // Remove empty project
    delete updatedProjects[projectId];
  } else {
    // Update project
    updatedProjects[projectId] = {
      ...project,
      designIds: updatedDesignIds,
      lastModified: new Date().toISOString()
    };
  }

  // Remove design
  const updatedDesigns = { ...designs };
  delete updatedDesigns[designId];

  // Update designIds array
  const updatedDesignIdsList = Object.keys(updatedDesigns);

  // Update current project and selected design if necessary
  let updatedCurrentProjectId = state.currentProjectId;
  let updatedSelectedDesignId = state.selectedDesignId;

  if (state.currentProjectId === projectId && updatedDesignIds.length === 0) {
    updatedCurrentProjectId = Object.keys(updatedProjects)[0] || null;
  }

  if (state.selectedDesignId === designId) {
    updatedSelectedDesignId = updatedDesignIdsList[0] || null;
  }

  return {
    ...state,
    projects: updatedProjects,
    designs: updatedDesigns,
    designIds: updatedDesignIdsList,
    currentProjectId: updatedCurrentProjectId,
    selectedDesignId: updatedSelectedDesignId
  };
};
